<template>
  <div class="account-settings">
    <full-screen-spinner v-if="initialLoading" />
    <m-content v-else>
      <page-header
        :title="$t('accountSettings.title')"
        boxed-xs
        heading="h3"
        underlined
        class="_header"
      />
      <m-content
        boxed-xs
        padding
        class="_content"
      >
        <m-section
          heading-size="h4"
          :title="$t('accountSettings.companyInfo.heading')"
          class="_settings _section"
        >
          <div class="_avatar">
            <m-avatar
              :size="120"
              :src="companyImage"
              class="_company-avatar"
              :text="loggedInUserAccount.companyName"
              shape="square"
            />
            <div class="_buttons">
              <m-tooltip :disabled="accountHasFeature([featureFlag.fileUpload])">
                <template #title>
                  {{ $t('fileUpload.featureDisabled') }}
                </template>
                <m-btn
                  :disabled="!accountHasFeature([featureFlag.fileUpload])"
                  @click="triggerInput"
                >
                  {{ $t('accountSettings.addLogo') }}
                </m-btn>
              </m-tooltip>

              <m-btn
                v-if="loggedInUserAccount.companyImage !== null"
                @click="deleteImage"
              >
                {{ $t('general.delete') }}
              </m-btn>
            </div>
          </div>
          <input
            ref="fileinput"
            type="file"
            :style="{display: 'none'}"
            accept="image/*"
            @change="fileChanged"
          >
          <m-dialog
            v-model:value="showCropImage"
            :title="$t('accountSettings.setCompanyPicture')"
            hide-footer
            keep-open-on-mask-click
            no-padding
          >
            <image-crop
              v-if="file !== null"
              class="_image-crop"
              :file="file"
              @cropped="uploadImage"
              @cancel="cancelImageCrop"
              @wrong-file-type="handleWrongFileType"
            />
          </m-dialog>
          <div
            class="_form"
          >
            <m-form-item
              :label="$t('accountSettings.companyInfo.nameLabel')"
            >
              <m-tooltip
                :force-show="companyNameField.hasError"
                :relocate-key="companyNameField.value"
                placement="left"
              >
                <m-text-field
                  v-model:value="companyNameField.value"
                  :has-error="companyNameField.hasError"
                  @blur="companyNameField.onBlur"
                />
                <template
                  v-if="companyNameField.hasError"
                  #title
                >
                  {{ companyNameField.error }}
                </template>
              </m-tooltip>
            </m-form-item>
            <m-form-item
              :label="$t('accountSettings.companyInfo.signUpPath')"
              class="_domain"
            >
              <div class="_inner">
                <div class="_top">
                  <div class="_signup-prefix">
                    {{ signupPathPrefix }}
                  </div>
                  <m-tooltip
                    :key="isSlugChangeThrottled"
                    :force-show="slugField.hasError"
                    :relocate-key="slugField.value"
                    placement="top"
                  >
                    <m-text-field
                      v-model:value="slugField.value"
                      :formatter="pathFormatter"
                      :has-error="slugField.hasError"
                      :disabled="isSlugChangeThrottled"
                      small
                      @focus="slugExists=false"
                      @blur="slugField.onBlur"
                    />
                    <template
                      v-if="slugField.hasError || isSlugChangeThrottled"
                      #title
                    >
                      <div v-if="slugField.hasError">
                        {{ slugField.error }}
                      </div>
                      <div v-if="isSlugChangeThrottled">
                        {{ $t('accountSettings.companyInfo.signUpPathThrottle') }}
                      </div>
                    </template>
                  </m-tooltip>
                  <div class="_signup-suffix">
                    {{ signupPathSuffix }}
                  </div>
                </div>
                <div class="_description">
                  <i18n-t
                    keypath="accountSettings.companyInfo.signUpPathDescription"
                    tag="span"
                    @click="showHint = true"
                  >
                    <template #link>
                      <m-tooltip
                        :style="{ display: 'inline-flex', color: $colors.grey.darken1, textDecoration: 'underline' }"
                      >
                        <span
                          v-clipboard="signUpLink"
                          v-clipboard:success="clipboardSuccessHandler"
                          v-clipboard:error="onError"
                        >
                          <m-link
                            underline-on-hover
                            background-on-hover
                            inherit-color
                          >
                            {{ signUpLink }}
                          </m-link>
                        </span>
                        <template #title>
                          {{ $t('accountSettings.clickToCopy') }}
                        </template>
                      </m-tooltip>
                    </template>
                  </i18n-t>
                  <m-dialog
                    v-model:value="showHint"
                    hide-header
                    hide-footer
                    max-width="30rem"
                    center
                  >
                    <div class="_invite-hint">
                      <div class="_icon">
                        <m-icon
                          type="warning"
                          :color="$colors.yellow.base"
                          size="40"
                        />
                      </div>
                      <div class="_hint-text">
                        {{ $t('accountSettings.companyInfo.inviteHint') }}
                      </div>
                      <m-btn
                        color="warning"
                        outlined
                        block
                        @click="showHint = false"
                      >
                        {{ $t('accountSettings.companyInfo.gotIt') }}
                      </m-btn>
                    </div>
                  </m-dialog>
                </div>
              </div>
            </m-form-item>
          </div>
          <m-btn
            class="_button"
            color="primary"
            :loading="loading"
            @click="submit"
          >
            {{ $t('general.save') }}
          </m-btn>
        </m-section>
        <m-section
          v-if="canManageModules"
          heading-size="h4"
          :title="$t('accountSettings.features.heading')"
          :sub-title="$t('accountSettings.features.subHeading')"
          class="_section"
        >
          <settings-switch-item
            :title="$t('accountSettings.features.goalsHeading')"
            :sub-title="$t('accountSettings.features.goalsDescription')"
            :value="accountSettings.usesGoals"
            :loading="loading"
            icon="compass"
            @click="showConfirm('goals', accountSettings.usesGoals)"
          />
          <m-divider small />
          <settings-switch-item
            :title="$t('accountSettings.features.updatesHeading')"
            :sub-title="$t('accountSettings.features.updatesDescription')"
            :value="accountSettings.usesUpdates"
            :loading="loading"
            icon="history"
            @click="showConfirm('updates', accountSettings.usesUpdates)"
          />
          <m-divider small />
        </m-section>
        <m-section
          v-if="accountHasFeature([experimentFlag.aiChatPilot])"
          heading-size="h4"
          :title="$t('accountSettings.aiChat.heading')"
          class="_section"
        >
          <settings-switch-item
            :title="$t('accountSettings.aiChat.title')"
            :sub-title="$t('accountSettings.aiChat.description')"
            :value="accountSettings.usesAiChat"
            :disabled="!canToggleFlag(featureFlag.aiChat)"
            @click="confirmAiChatToggle"
          />
        </m-section>
        <company-info
          class="_section"
        />
        <div class="_section">
          <settings-action-row>
            <template #title>
              {{ $t('accountSettings.reset.heading') }}
            </template>
            <template #description>
              {{ $t('accountSettings.reset.subHeading') }}
            </template>
            <m-btn
              class="_button"
              @click="showResetModal = true"
            >
              {{ $t('accountSettings.reset.button') }}
            </m-btn>
          </settings-action-row>
          <m-dialog
            v-model:value="showResetModal"
            hide-footer
            no-padding
          >
            <reset-account-card @close="showResetModal = false" />
          </m-dialog>
        </div>
        <danger-zone
          v-if="canDeleteAccount"
          class="_section"
        >
          <settings-action-row>
            <template #title>
              {{ $t('accountSettings.deleteWorkspace.prompt') }}
            </template>
            <template #description>
              {{ $t('accountSettings.deleteWorkspace.subHeading') }}
            </template>
            <m-tooltip :disabled="isDeletable">
              <template #title>
                {{ $t('accountSettings.deleteWorkspace.activeAccountHint') }}
              </template>
              <m-btn
                class="_button"
                light
                color="danger"
                :disabled="!isDeletable"
                @click="deleteAccount"
              >
                {{ $t('accountSettings.deleteWorkspace.prompt') }}
              </m-btn>
            </m-tooltip>
          </settings-action-row>
        </danger-zone>
      </m-content>
    </m-content>
  </div>
</template>

<script>
import CompanyInfo from '@/components/navigation/CompanyInfo.vue';
import DangerZone from '@/components/DangerZone.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import ImageCrop from '@/components/ImageCrop.vue';
import PageHeader from 'shared/components/PageHeader.vue';
import ResetAccountCard from '@/components/ResetAccountCard.vue';
import SettingsActionRow from '@/components/SettingsActionRow.vue';
import SettingsSwitchItem from '@/components/SettingsSwitchItem.vue';
import useAccess from '@/composables/access/access';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useAccountSlugs from '@/composables/logged-in-user-account/account-slug';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import { accessGroupFlag, failureCode, featureFlag, routeName } from 'shared/constants.json';
import { computed, ref } from 'vue';
import { experimentFlag } from 'shared/experiments.json';
import { failureCodeOf, logCatch } from '@/lib/logger/logger';
import { useCompanyNameField, useValidatedField } from '@/composables/field-validation';

export default {
  name: 'AccountSettings',
  components: {
    SettingsSwitchItem,
    SettingsActionRow,
    ResetAccountCard,
    FullScreenSpinner,
    PageHeader,
    ImageCrop,
    CompanyInfo,
    DangerZone,
  },
  setup() {
    const { accountHasFeature, userHasRight } = useAccess();
    const accountSvc = useLoggedInUserAccount();
    const { accountSettings, canToggleFlag, toggleFlag } = useAccountSettings();
    const { isDeletable } = accountSvc;
    const canDeleteAccount = computed(() => userHasRight([accessGroupFlag.accountWriteAccess]));

    const companyNameField = useCompanyNameField(ref(''));

    const { slugs } = useAccountSlugs();
    const isSlugChangeThrottled = computed(() => {
      if (slugs.value.length === 0) {
        return false;
      }
      const now = Date.now();
      const DAY = 24 * 60 * 60 * 1000;
      return slugs.value.some((s) => s.createdAt && (now - new Date(s.createdAt).getTime()) < DAY);
    });
    const slugExists = ref(false);
    const validate = (slugExists) => (value, t) => {
      if (slugExists.value) {
        return t('accountSettings.nameTaken');
      }
      return '';
    };
    const slugField = useValidatedField(ref(''), validate(slugExists));

    return {
      loggedInUserAccount: accountSvc.loggedInUserAccount,
      accountSvc,
      accountSettings,
      canToggleFlag,
      toggleFlag,
      isDeletable,
      canDeleteAccount,
      featureFlag,
      experimentFlag,
      accountHasFeature,
      companyNameField,
      slugExists,
      slugField,
      isSlugChangeThrottled,
    };
  },
  data() {
    const link = this.$router.resolve({ name: routeName.signUp });
    return {
      signupPathPrefix: `${window.location.host}/#/`,
      signupPathSuffix: `${link.fullPath}`,
      canManageModules: false,
      loading: false,
      initialLoading: false,
      showResetModal: false,
      file: null,
      showCropImage: false,
      showHint: false,
    };
  },
  computed: {
    signUpLink() {
      const link = this.$router.resolve({ name: routeName.signUpLandingPage, params: { slug: this.loggedInUserAccount.activeSlug.slug } });
      return `${window.location.host}/${link.href}`;
    },
    companyImage() {
      if (this.loggedInUserAccount.companyImage === null) {
        return '';
      }
      return this.loggedInUserAccount.companyImage.getURL;
    },
  },
  methods: {
    deleteImage() {
      const deleteImage = () => {
        this.accountSvc.deleteImage().catch(logCatch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }));
      };
      this.$confirm({
        title: this.$t('accountSettings.deleteImagePrompt.content'),
        okText: this.$t('accountSettings.deleteImagePrompt.okText'),
        okType: 'danger',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          deleteImage();
        },
      });
    },
    deleteAccount() {
      const performDelete = (setLoading) => {
        setLoading(true);
        return this.accountSvc.deleteAccount()
          .then((response) => {
            if (response.status !== 200) {
              throw new Error(response.response?.data);
            }
            window.location.assign('/');
          })
          .catch(logCatch(() => {
            setLoading(false);
            this.$hideConfirm();
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          }));
      };

      const confirmFinal = () => {
        this.$confirm({
          title: this.$t('accountSettings.deleteWorkspace.confirmQuestion'),
          okText: this.$t('accountSettings.deleteWorkspace.confirmButton'),
          okType: 'danger',
          maskClosable: false,
          hideOnSubmit: false,
          cancelText: this.$t('general.cancel'),
          onOk: performDelete,
        });
      };

      this.$confirm({
        title: this.$t('accountSettings.deleteWorkspace.prompt'),
        message: { text: this.$t('accountSettings.deleteWorkspace.warningMessage'), type: 'error', icon: 'exclamation-circle-filled' },
        secure: {
          question: this.$t('general.typeWorkspaceNameToProceed'),
          answer: this.loggedInUserAccount.companyName,
        },
        okText: this.$t('general.continue'),
        okType: 'danger',
        maskClosable: false,
        hideOnSubmit: false,
        cancelText: this.$t('general.cancel'),
        onOk: confirmFinal,
      });
    },
    clipboardSuccessHandler() {
      this.$showSnackbar({ color: 'success', message: this.$t('success.copied') });
    },
    onError() {
      this.$showSnackbar({ color: 'error', message: this.$t('error.duringCopying') });
    },
    pathFormatter(value) {
      let v = value;
      v = v.toLowerCase();
      v = v.replace('ö', 'oe');
      v = v.replace('ä', 'ae');
      v = v.replace('ü', 'ue');
      v = v.replace('ß', 'ss');
      v = v.replace(/\s/g, '-');
      v = v.replace(/[^a-zA-Z0-9_-]/g, '');
      return v;
    },
    triggerInput() {
      this.$refs.fileinput.click();
    },
    uploadImage(blob) {
      this.accountSvc.uploadImage(blob).then(() => {
        this.file = null;
        this.$showSnackbar({ color: 'success', message: this.$t('success.updated') });
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      })).finally(() => {
        this.showCropImage = false;
      });
    },
    cancelImageCrop() {
      this.showCropImage = false;
      this.file = null;
    },
    handleWrongFileType() {
      this.showCropImage = false;
      this.file = null;
      this.$showSnackbar({ color: 'error', message: this.$t('error.notAnImage') });
    },
    fileChanged(event) {
      if (event.target.files.length === 0) {
        return;
      }

      this.file = event.target.files[0];
      this.showCropImage = true;
      this.$refs.fileinput.value = '';
    },
    submit() {
      this.companyNameField.onSubmit();
      this.slugField.onSubmit();
      if (!this.companyNameField.isValid || !this.slugField.isValid) {
        return;
      }
      this.loading = true;
      this.accountSvc.updateSingle({
        uid: this.loggedInUserAccount.uid,
        companyName: this.companyNameField.value,
        activeSlug: { slug: this.slugField.value },
      })
        .then(() => {
          this.slugExists = false;
          this.$showSnackbar({ color: 'success', message: this.$t('success.updated') });
        })
        .catch(logCatch((e) => {
          if (failureCodeOf(e) === failureCode.slugExists) {
            this.slugExists = true;
            return;
          }
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }, [failureCode.slugExists]))
        .finally(() => {
          this.loading = false;
        });
    },
    updateSettings({ appIntegration, accountSettings }) {
      const updateEntity = { uid: this.loggedInUserAccount.uid, accountSettings };
      if (appIntegration != null) {
        updateEntity.appIntegration = appIntegration;
      }

      this.loading = true;
      this.accountSvc.updateSingle(updateEntity)
        .then(() => {
          this.$showSnackbar({ color: 'success', message: this.$t('success.updated') });
        })
        .catch(logCatch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }))
        .finally(() => {
          this.loading = false;
        });
    },
    showConfirm(type, isActive) {
      const update = this.updateSettings;
      let title = '';
      let content = '';
      const accountSettings = { ...this.accountSettings };
      const appIntegration = null;
      switch (type) {
        case 'goals': {
          title = isActive ? this.$t('accountSettings.features.deactivateGoals.title') : this.$t('accountSettings.features.activateGoals.title');
          content = isActive ? this.$t('accountSettings.features.deactivateGoals.content') : this.$t('accountSettings.features.activateGoals.content');
          accountSettings.usesGoals = !accountSettings.usesGoals;
          break;
        }
        case 'updates': {
          title = isActive ? this.$t('accountSettings.features.deactivateUpdates.title') : this.$t('accountSettings.features.activateUpdates.title');
          content = isActive ? this.$t('accountSettings.features.deactivateUpdates.content') : this.$t('accountSettings.features.activateUpdates.content');
          accountSettings.usesUpdates = !accountSettings.usesUpdates;
          break;
        }
        default:
      }
      this.$confirm({
        title,
        message: { text: content },
        okText: isActive ? this.$t('accountSettings.features.deactivate') : this.$t('accountSettings.features.activate'),
        okType: isActive ? 'danger' : 'primary',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          update({ accountSettings, appIntegration });
        },
      });
    },
    confirmAiChatToggle() {
      const isActive = this.accountSettings.usesAiChat;
      const toggle = () => this.toggleFlag(featureFlag.aiChat).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      }));
      this.$confirm({
        title: isActive ? this.$t('accountSettings.aiChat.deactivate.title') : this.$t('accountSettings.aiChat.activate.title'),
        message: { text: isActive ? this.$t('accountSettings.aiChat.deactivate.content') : this.$t('accountSettings.aiChat.activate.content') },
        okText: isActive ? this.$t('accountSettings.features.deactivate') : this.$t('accountSettings.features.activate'),
        okType: isActive ? 'danger' : 'primary',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk: toggle,
      });
    },
    showReset() {
      if (typeof this.$route.query.showResetModal !== 'undefined') {
        this.showResetModal = true;
      }
    },
  },
  created() {
    this.showReset();
    this.companyNameField.value = this.loggedInUserAccount.companyName;
    this.slugField.value = this.loggedInUserAccount.activeSlug.slug;
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .account-settings {
    ._header {
      margin-bottom: 2rem;
    }

    ._content {
      padding-bottom: 4rem;
    }

    ._section {
      margin-bottom: 4rem;
    }

    ._item {
      margin-bottom: 1.4rem;

      ._label {
        margin-bottom: .4rem;
        color: $font-color-secondary;
      }
    }

    ._domain {
      ._inner {
        display: flex;
        flex-direction: column;

        ._top {
          display: flex;

          ._signup-prefix {
            display: flex;
            flex: 0 0 auto;
            align-items: center;
            margin-right: .4rem;
          }

          ._signup-suffix {
            display: flex;
            flex: 0 0 auto;
            align-items: center;
            margin-left: .4rem;
          }
        }

        ._description {
          display: flex;
          font-size: $font-size-3;
          line-height: 1.5;
          color: $font-color-secondary;
        }
      }
    }

    ._settings {
      ._avatar {
        margin-top: 2rem;
        margin-bottom: 2rem;

        ._company-avatar {
          margin-bottom: 1rem;
        }
      }

      ._buttons {
        display: flex;
        gap: .8rem;
      }
    }
  }

  ._invite-hint {
    ._icon {
      margin-bottom: 1.2rem;
    }

    ._hint-text {
      margin-bottom: 2rem;
    }
  }
</style>
